import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subscription, distinctUntilChanged, filter } from "rxjs";
import { v4 } from "uuid";

import {
    ComparisonTable,
    MeasurementSystem,
    MeasurementSystemSizeComparisonTable,
    MeasureYourselfTable,
} from "@hermes/api-model-product";

import { UnitTypeService } from "../../services/unit-type.service";

const SIZE_COMPARAISON_TABLE = $localize`:@@hermes.size_guide.size_comparaison_table:Find your size`;

@Component({
    selector: "h-block-table",
    templateUrl: "./block-table.component.html",
    styleUrl: "./block-table.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockTableComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true })
    public content!:
        | MeasurementSystemSizeComparisonTable
        | Partial<MeasureYourselfTable>;

    public subscription: Subscription = new Subscription();

    public currentContent!: ComparisonTable | undefined;

    public labelInt: string = "cm";
    public labelImp: string = "inches";

    public textForTableCaption: string = SIZE_COMPARAISON_TABLE;

    public selectedUnitForm: FormGroup = this.fb.group({
        unitTypeControl: undefined,
    });

    public uuidForm = v4();

    public translations: { [key: string]: string } = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "hermes.size_guide.size_comparaison_table_cm": $localize`:@@hermes.size_guide.size_comparaison_table_cm:Size guide table in centimeter`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "hermes.size_guide.size_comparaison_table_mm": $localize`:@@hermes.size_guide.size_comparaison_table_mm:Size guide table in millimeter`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "hermes.size_guide.size_comparaison_table_inches": $localize`:@@hermes.size_guide.size_comparaison_table_inches:Size guide table in inch`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "hermes.size_guide.size_comparaison_table_foot": $localize`:@@hermes.size_guide.size_comparaison_table_foot:Size guide table in foot`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "hermes.size_guide.size_comparaison_table": $localize`:@@hermes.size_guide.size_comparaison_table:Find your size`,
    };

    constructor(
        private fb: FormBuilder,
        private unitTypeService: UnitTypeService,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit() {
        this.subscription.add(
            this.unitTypeService.selectedUnit$
                .pipe(
                    distinctUntilChanged(),
                    filter(
                        (unit) =>
                            unit !==
                            this.selectedUnitForm.get("unitTypeControl")?.value,
                    ),
                )
                .subscribe((unit) => {
                    this.selectedUnitForm.patchValue({ unitTypeControl: unit });
                    this.setTableCurrentContent(unit);
                    this.cdr.detectChanges();
                }),
        );
        this.onUnitTypeChangeInForm();
    }

    public ngOnChanges(_changes: SimpleChanges): void {
        this.setTableCurrentContent(
            this.selectedUnitForm.get("unitTypeControl")?.value,
        );
    }

    public getTranslation(key: string): string {
        return this.translations[key] || key;
    }

    public getTranslationKey = (
        label: string,
    ): { translationKey: string; translationValue: string } => {
        const unitMapping: { [key: string]: string } = {
            cm: "centimeter",
            mm: "millimeter",
            inches: "inch",
            foot: "foot",
        };

        let translationKey;
        let translationValue;

        const translatedLabel = unitMapping[label];
        if (translatedLabel) {
            translationKey = `hermes.size_guide.size_comparaison_table_${label}`;
            translationValue = `Size guide table in ${translatedLabel}`;
            return { translationKey, translationValue };
        }
        translationKey = "hermes.size_guide.size_comparaison_table";
        translationValue = "Size guide table";
        return { translationKey, translationValue };
    };

    public setTableCurrentContent(unit: MeasurementSystem): void {
        this.currentContent = this.content[unit];
        this.labelInt = this.content["international"]?.label || "cm";
        this.labelImp = this.content["imperial"]?.label || "inches";

        const tableCaptions = {
            international: this.getTranslationKey(this.labelInt),
            imperial: this.getTranslationKey(this.labelImp),
            default: {
                translationKey: "hermes.size_guide.size_comparaison_table",
                translationValue: "Size guide table",
            },
        };

        const selectedCaptionKey = tableCaptions[unit] || tableCaptions.default;

        this.textForTableCaption = this.getTranslation(
            selectedCaptionKey.translationKey,
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private onUnitTypeChangeInForm() {
        const unitTypeControl = this.selectedUnitForm.get("unitTypeControl");
        if (unitTypeControl) {
            this.subscription.add(
                unitTypeControl.valueChanges
                    .pipe(filter(Boolean))
                    .subscribe((selectedValue: MeasurementSystem) => {
                        this.unitTypeService.selectedUnit$.next(selectedValue);
                        this.setTableCurrentContent(selectedValue);
                    }),
            );
        }
    }
}
